import { useState, useEffect } from "react";
import api from "../../../utils/api";

import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    OutlinedInput,
    FormHelperText,
    Button,
    Box,
    FormControl,
    FormLabel,
    CircularProgress,
    Tabs,
    Tab,
    Avatar
} from "@mui/material";
import QuillEditor from "../../../components/QuillEditor";

const languages = ['hy', 'en', 'ru'];
const languageLabels = {
    'hy': 'Armenian',
    'en': 'English',
    'ru': 'Russian'
};

const textFields = ['title', 'description', 'meta_keywords', 'meta_description'];

export default function BlogModal({ open, onClose, row = null }) {
    const [saving, setSaving] = useState(false);
    const [errors, setErrors] = useState({});
    const [activeTab, setActiveTab] = useState(0);
    const [form, setForm] = useState(() => {
        const initial = { image: null };
        languages.forEach(lang => {
            textFields.forEach(field => {
                initial[`${field}_${lang}`] = '';
            });
        });
        return initial;
    });
    const [imagePreview, setImagePreview] = useState(null);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setForm((prev) => ({ ...prev, [name]: value }));
    };

    const handleDescriptionChange = (language, value) => {
        setForm((prev) => ({ ...prev, [`description_${language}`]: value }));
    };

    const handleImageChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            setForm((prev) => ({ ...prev, image: file }));
            // Create preview
            const reader = new FileReader();
            reader.onloadend = () => {
                setImagePreview(reader.result);
            };
            reader.readAsDataURL(file);
        }
    };

    const handleSubmit = async () => {
        try {
            setSaving(true);
            setErrors({});

            const formData = new FormData();
            
            // Add all multilingual fields
            languages.forEach(lang => {
                textFields.forEach(field => {
                    const key = `${field}_${lang}`;
                    if (form[key]) {
                        formData.append(key, form[key]);
                    }
                });
            });

            // Add image if it's a new file
            if (form.image instanceof File) {
                formData.append('image', form.image);
            } else if (!row && !form.image) {
                // Image is required for new blog posts
                setErrors({ image: ['Image is required'] });
                setSaving(false);
                return;
            }

            if (row?.id) {
                // Use POST route for updates with FormData
                await api.post(`admin/blog/${row.id}/update`, formData, {
                    headers: {
                        'Content-Type': 'multipart/form-data',
                    },
                });
            } else {
                await api.post("admin/blog", formData, {
                    headers: {
                        'Content-Type': 'multipart/form-data',
                    },
                });
            }

            onClose(true);
        } catch (e) {
            if (e.errors) {
                setErrors(e.errors);
            } else if (e.response?.data?.errors) {
                setErrors(e.response.data.errors);
            }
        } finally {
            setSaving(false);
        }
    };

    useEffect(() => {
        if (row) {
            const newForm = { image: null };
            languages.forEach(lang => {
                textFields.forEach(field => {
                    newForm[`${field}_${lang}`] = row[`${field}_${lang}`] || '';
                });
            });
            setForm(newForm);
            // Set image preview if exists
            if (row.image) {
                setImagePreview(row.image.startsWith('http') ? row.image : `/storage/${row.image}`);
            } else {
                setImagePreview(null);
            }
        } else {
            const initial = { image: null };
            languages.forEach(lang => {
                textFields.forEach(field => {
                    initial[`${field}_${lang}`] = '';
                });
            });
            setForm(initial);
            setImagePreview(null);
        }
        setErrors({});
        setSaving(false);
        setActiveTab(0);
    }, [row, open]);

    const currentLanguage = languages[activeTab];

    return (
        <Dialog open={open} onClose={() => onClose()} maxWidth="md" fullWidth>
            <DialogTitle>{!row ? "Create Blog Post" : "Edit Blog Post"}</DialogTitle>
            <DialogContent>
                <Box display="flex" flexDirection="column" gap={2} mt={1}>
                    {/* Language Tabs */}
                    <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
                        <Tabs value={activeTab} onChange={(e, newValue) => setActiveTab(newValue)}>
                            {languages.map((lang, index) => (
                                <Tab key={lang} label={languageLabels[lang]} />
                            ))}
                        </Tabs>
                    </Box>

                    {/* Image Upload */}
                    <FormControl error={!!errors.image?.[0]}>
                        <FormLabel htmlFor="image">Image {!row && '*'}</FormLabel>
                        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                            {imagePreview && (
                                <Avatar
                                    variant="rounded"
                                    src={imagePreview}
                                    sx={{ width: 100, height: 100 }}
                                />
                            )}
                            <Button variant="outlined" component="label">
                                {row && !form.image ? 'Change Image' : 'Upload Image'}
                                <input
                                    type="file"
                                    hidden
                                    accept="image/*"
                                    onChange={handleImageChange}
                                />
                            </Button>
                        </Box>
                        {errors.image && (
                            <FormHelperText sx={{ m: 0.5 }} error={!!errors.image}>
                                {errors.image[0]}
                            </FormHelperText>
                        )}
                    </FormControl>

                    {/* Multilingual Fields for Current Language */}
                    <FormControl error={!!errors[`title_${currentLanguage}`]?.[0]}>
                        <FormLabel htmlFor={`title_${currentLanguage}`}>Title *</FormLabel>
                        <OutlinedInput
                            name={`title_${currentLanguage}`}
                            fullWidth
                            value={form[`title_${currentLanguage}`] || ''}
                            onChange={handleChange}
                        />
                        {errors[`title_${currentLanguage}`] && (
                            <FormHelperText sx={{ m: 0.5 }} error={!!errors[`title_${currentLanguage}`]}>
                                {errors[`title_${currentLanguage}`][0]}
                            </FormHelperText>
                        )}
                    </FormControl>

                    <FormControl error={!!errors[`description_${currentLanguage}`]?.[0]} fullWidth>
                        <FormLabel sx={{ mb: 1 }}>Content ({languageLabels[currentLanguage]}) *</FormLabel>
                        <QuillEditor
                            key={`description-editor-${currentLanguage}`}
                            value={form[`description_${currentLanguage}`] || ''}
                            onChange={(value) => handleDescriptionChange(currentLanguage, value)}
                            placeholder={`Write ${languageLabels[currentLanguage]} content...`}
                        />
                        {errors[`description_${currentLanguage}`] && (
                            <FormHelperText sx={{ m: 0.5 }} error={!!errors[`description_${currentLanguage}`]}>
                                {errors[`description_${currentLanguage}`][0]}
                            </FormHelperText>
                        )}
                    </FormControl>

                    <FormControl error={!!errors[`meta_keywords_${currentLanguage}`]?.[0]}>
                        <FormLabel htmlFor={`meta_keywords_${currentLanguage}`}>Meta Keywords</FormLabel>
                        <OutlinedInput
                            name={`meta_keywords_${currentLanguage}`}
                            fullWidth
                            value={form[`meta_keywords_${currentLanguage}`] || ''}
                            onChange={handleChange}
                        />
                        {errors[`meta_keywords_${currentLanguage}`] && (
                            <FormHelperText sx={{ m: 0.5 }} error={!!errors[`meta_keywords_${currentLanguage}`]}>
                                {errors[`meta_keywords_${currentLanguage}`][0]}
                            </FormHelperText>
                        )}
                    </FormControl>

                    <FormControl error={!!errors[`meta_description_${currentLanguage}`]?.[0]}>
                        <FormLabel htmlFor={`meta_description_${currentLanguage}`}>Meta Description</FormLabel>
                        <OutlinedInput
                            name={`meta_description_${currentLanguage}`}
                            fullWidth
                            multiline
                            rows={3}
                            value={form[`meta_description_${currentLanguage}`] || ''}
                            onChange={handleChange}
                        />
                        {errors[`meta_description_${currentLanguage}`] && (
                            <FormHelperText sx={{ m: 0.5 }} error={!!errors[`meta_description_${currentLanguage}`]}>
                                {errors[`meta_description_${currentLanguage}`][0]}
                            </FormHelperText>
                        )}
                    </FormControl>
                </Box>
            </DialogContent>
            <DialogActions>
                <Button onClick={() => onClose()}>Cancel</Button>
                <Button
                    sx={{ color: '#fff' }}
                    variant="contained"
                    onClick={handleSubmit}
                    disabled={saving}
                >
                    {saving ? <CircularProgress size={24} /> : (!row ? "Create" : "Update")}
                </Button>
            </DialogActions>
        </Dialog>
    );
}
